<template>
  <div class="min-h-screen bg-off-black">
    <div class="container mx-auto px-4 py-16">
      <!-- Hero Section -->
      <div class="text-center mb-16">
        <h1 class="text-5xl md:text-6xl font-serif font-semibold mb-6 text-vibe-green">
          Transform Your Client Onboarding
        </h1>
        <p class="text-xl md:text-2xl text-gray-green max-w-3xl mx-auto">
          Streamline compliance, reduce risk, and accelerate client onboarding with AI-powered solutions for legal and accounting professionals
        </p>
      </div>

      <!-- Freebie Cards Section -->
      <div class="max-w-6xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-serif font-semibold text-white text-center mb-12">
          Free Resources
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <!-- Beneficial Ownership Toolkit Card - NEW -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-vibe-green hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105 relative">
            <div class="absolute top-4 right-4 bg-vibe-green text-black text-xs font-bold px-3 py-1 rounded-full">
              NEW
            </div>
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                2026 Beneficial Ownership Verification Toolkit
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                Practical toolkit for UK firms implementing the 2026 beneficial ownership reforms with clear, defensible steps.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Identify & verify beneficial owners</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Assess control, influence & economic reality</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Challenge inconsistencies and document reasoning</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Structured templates for BO, overseas entities & trusts</span>
                </li>
              </ul>

              <router-link
                to="/bo-verification-toolkit"
                class="btn-primary w-full text-center inline-block"
              >
                Download Free Toolkit
              </router-link>
            </div>
          </div>

          <!-- CDD Compliance Checklist Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105">
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                2026 CDD Compliance Checklist
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                Comprehensive checklist for UK firms implementing the new 2026 CDD standards with clear, actionable steps.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Client Identification & Verification</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Beneficial Ownership (BO) Checks</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Enhanced Due Diligence (EDD) triggers</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Source of Funds & Source of Wealth</span>
                </li>
              </ul>

              <router-link
                to="/cdd-compliance-checklist"
                class="btn-primary w-full text-center inline-block"
              >
                Download Free Checklist
              </router-link>
            </div>
          </div>

          <!-- Smart Onboarding Guide Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105">
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                Smart Onboarding Guide
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                A practical guide to AML compliance, risk management, and onboarding efficiency for legal and accounting professionals.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Risk-based onboarding strategies</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Sector-specific insights</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>OSINT & HUMINT techniques</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>ECCTA reforms overview</span>
                </li>
              </ul>

              <router-link
                to="/smart-onboarding-download"
                class="btn-primary w-full text-center inline-block"
              >
                Download Free Guide
              </router-link>
            </div>
          </div>

          <!-- ACSP ID&V Training Video Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105">
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                ACSP ID&V Training Video
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                Comprehensive training video covering ID verification procedures and compliance requirements for ACSPs.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>ID verification best practices</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Compliance requirements</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Step-by-step guidance</span>
                </li>
              </ul>

              <router-link
                to="/acsp-training-video"
                class="btn-primary w-full text-center inline-block"
              >
                Watch Training Video
              </router-link>
            </div>
          </div>

          <!-- Understanding Professional Enablement Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105 relative">
            <div class="absolute top-4 right-4 bg-vibe-green text-black text-xs font-bold px-3 py-1 rounded-full">
              NEW
            </div>
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                Understanding Professional Enablement
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                A staff information sheet to help professional services firms recognise and prevent enablement risks.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>What enablement means & why it matters</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Three forms: deliberate, wilful blindness, accidental</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Real-world scenarios in practice</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Self-assessment questions to counter risk</span>
                </li>
              </ul>

              <router-link
                to="/professional-enablement"
                class="btn-primary w-full text-center inline-block"
              >
                Download Free Info Sheet
              </router-link>
            </div>
          </div>

          <!-- Marker AI 6 Minute Demo Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105">
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                Marker AI 6 Minute Demo
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                Quick overview of the Marker AI platform showcasing key features and how it transforms client onboarding.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Platform walkthrough</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Key features demo</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Real-world use cases</span>
                </li>
              </ul>

              <router-link
                to="/marker-demo-video"
                class="btn-primary w-full text-center inline-block"
              >
                Watch Demo Video
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="max-w-4xl mx-auto mt-20 bg-green-600 rounded-lg p-12 text-center border border-vibe-green">
        <h2 class="text-3xl md:text-4xl font-serif font-semibold text-vibe-green mb-6">
          Ready to Automate Your Onboarding?
        </h2>
        <p class="text-lg text-white mb-8">
          See how Marker AI can help you streamline workflows, strengthen compliance, and accelerate commercial momentum. Book a demo to learn more.
        </p>
        <a
          href="https://www.marker-ai.com/contact-us"
          class="btn-primary text-lg"
        >
          Get in Touch
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
};
</script>

<style scoped>
/* Additional component-specific styles if needed */
</style>
